import React, { useMemo } from 'react';

import Button from 'components/shared/Button';
import SpecialContentMenuItem from './SpecialContentMenuItem';

import slug from 'utils/slug';

import type { SpecialContentMenuItemData } from './SpecialContentMenuItem';

export type SpecialContentMenuProps = {
  items: SpecialContentMenuItemData[];
  title: string;
  previousItemIds: string[];
  itemIndex: number;
  containerPadding: number;
  handleDone: () => void;
  handleCloseMenu: () => void;
  handleViewManage?: () => void;
};

function SpecialContentMenu({
  previousItemIds,
  itemIndex,
  title,
  containerPadding,
  items,
  handleDone,
  handleCloseMenu,
  handleViewManage,
}: SpecialContentMenuProps) {
  // Get the sum height of all the previous list items above this one
  // so we can correctly position the menu.
  // Add 44 * itemIndex for the amount of primary buttons above it: special_content_sidebar.scss
  // Add 10 more for the padding on this list item: special_content_sidebar.scss
  // Add amount from props for padding on the whole sidebar: EditorSidebar.js
  const menuStyle = useMemo(() => {
    const previousItems = previousItemIds.map((id) => document.getElementById(id) as HTMLElement);

    const previousGroupsOffset = previousItems.reduce((acc, el) => {
      const { height } = el.getBoundingClientRect();
      return acc + height;
    }, 0);

    const menuOffset = previousGroupsOffset + itemIndex * 44 + 10 + containerPadding;

    return {
      top: `${menuOffset}px`,
      height: `calc(100% - ${menuOffset}px)`,
    };
  }, [previousItemIds, itemIndex, containerPadding]);

  const menuItems = useMemo(
    () =>
      items.map((item) => (
        <SpecialContentMenuItem
          key={item.id}
          {...item}
          parent={slug(title)}
          handleCloseMenu={handleCloseMenu}
        />
      )),
    [items, handleCloseMenu]
  );

  function onDone() {
    handleCloseMenu();
    handleDone();
  }

  function onViewManage() {
    handleCloseMenu();
    handleViewManage && handleViewManage();
  }

  return (
    <div className="special-content-menu" style={menuStyle}>
      <div className="menu-header">
        <Button
          className="close"
          type="icon"
          icon="times"
          a11yText="Close menu"
          onClick={handleCloseMenu}
        />
        <h2>{title}</h2>
      </div>
      <div className="menu-body">
        {menuItems.length === 0 && <p>You have no {title.toLowerCase()}</p>}
        {menuItems.length > 0 && <ul aria-label={`list of ${title}`}>{menuItems}</ul>}
      </div>
      <div className="menu-footer">
        <Button onClick={onDone}>DONE</Button>
        {handleViewManage && (
          <Button type="bare" onClick={onViewManage}>
            View / Manage
          </Button>
        )}
      </div>
    </div>
  );
}

export default SpecialContentMenu;
